import React, { useState, useEffect } from 'react';
import { Button, Toast } from 'antd-mobile';
import { useStore } from '../store/useStore';
import { theme } from '../theme';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

interface InviteInfo {
    ownerName: string;
    ownerPicture: string;
    label: string;
}

interface Props {
    code: string;
    onDone: () => void;
}

export const AcceptInvite: React.FC<Props> = ({ code, onDone }) => {
    const { user } = useStore();
    const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Fetch invite info
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/invite/${code}/info`);
                if (res.ok) {
                    const data = await res.json();
                    setInviteInfo(data);
                } else {
                    setError('คำเชิญนี้หมดอายุหรือไม่พบ');
                }
            } catch {
                setError('ไม่สามารถโหลดข้อมูลได้');
            } finally {
                setLoading(false);
            }
        };
        fetchInfo();
    }, [code]);

    const handleAccept = async () => {
        if (!user?.id) {
            Toast.show({ content: 'กรุณาเข้าสู่ระบบก่อน', icon: 'fail' });
            return;
        }
        setAccepting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/invite/${code}/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    viewerUserId: user.id,
                    viewerName: user.displayName,
                    viewerPicture: (user as any).pictureUrl || '',
                }),
            });
            if (res.ok) {
                setSuccess(true);
                Toast.show({ content: 'เชื่อมต่อสำเร็จ!', icon: 'success' });
                // Redirect to LINE OA chat (ปอดดี)
                setTimeout(() => {
                    const lineOaId = import.meta.env.VITE_LINE_OA_ID || '@511pqziw';
                    window.location.href = `https://line.me/R/ti/p/${lineOaId}`;
                }, 1500);
            } else {
                const data = await res.json().catch(() => ({}));
                setError((data as any).error || 'ไม่สามารถตอบรับได้');
            }
        } catch {
            setError('เกิดข้อผิดพลาด กรุณาลองใหม่');
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 100%)',
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 16, animation: 'pulse 1.5s infinite' }}>💌</div>
                    <div style={{ color: '#64748b', fontSize: 14 }}>กำลังโหลดคำเชิญ...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(180deg, #fef2f2 0%, #fff1f2 100%)',
                padding: 24,
            }}>
                <div style={{ textAlign: 'center', maxWidth: 320 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#991b1b', margin: '0 0 8px' }}>
                        ไม่พบคำเชิญ
                    </h2>
                    <p style={{ color: '#dc2626', fontSize: 14, margin: '0 0 24px' }}>{error}</p>
                    <Button
                        onClick={onDone}
                        style={{
                            borderRadius: 24, padding: '10px 32px',
                            background: '#f1f5f9', border: 'none', color: '#475569',
                            fontWeight: 600,
                        }}
                    >
                        กลับหน้าหลัก
                    </Button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 100%)',
                padding: 24,
            }}>
                <div style={{ textAlign: 'center', maxWidth: 320 }}>
                    <div style={{
                        width: 80, height: 80, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px', fontSize: 36,
                        boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
                    }}>✓</div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#166534', margin: '0 0 8px' }}>
                        เชื่อมต่อสำเร็จ! 🎉
                    </h2>
                    <p style={{ color: '#15803d', fontSize: 14, margin: '0 0 8px' }}>
                        คุณเป็นผู้ดูแลของ <strong>{inviteInfo?.ownerName}</strong> แล้ว
                    </p>
                    <p style={{ color: '#15803d', fontSize: 12, margin: '0 0 4px' }}>
                        พิมพ์ "วันนี้" หรือ "สถิติ" ใน LINE เพื่อดูข้อมูล
                    </p>
                    <p style={{ color: '#86efac', fontSize: 12 }}>กำลังพากลับไปที่ LINE...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #e0f2fe 0%, #dbeafe 50%, #f0f9ff 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24,
        }}>
            {/* Card */}
            <div style={{
                background: 'white',
                borderRadius: 24,
                padding: '40px 28px 32px',
                maxWidth: 340, width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                textAlign: 'center',
            }}>
                {/* Envelope icon */}
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fecdd3, #fda4af)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '-60px auto 20px', fontSize: 28,
                    boxShadow: '0 8px 24px rgba(253, 164, 175, 0.4)',
                    border: '4px solid white',
                }}>💌</div>

                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>
                    คำเชิญจาก
                </h2>

                {/* Owner profile */}
                <div style={{
                    margin: '20px 0',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                    <div style={{
                        padding: 3, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                        marginBottom: 12,
                    }}>
                        <img
                            src={inviteInfo?.ownerPicture || 'https://placehold.co/100x100/3b82f6/white?text=P'}
                            alt={inviteInfo?.ownerName}
                            style={{
                                width: 72, height: 72, borderRadius: '50%',
                                objectFit: 'cover', border: '3px solid white',
                            }}
                        />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                        {inviteInfo?.ownerName}
                    </div>
                    <div style={{
                        marginTop: 8, padding: '4px 14px', borderRadius: 20,
                        background: '#f0f9ff', fontSize: 12, color: '#0284c7', fontWeight: 600,
                    }}>
                        {inviteInfo?.label || 'ครอบครัว'}
                    </div>
                </div>

                {/* Description */}
                <div style={{
                    background: '#f8fafc', borderRadius: 14, padding: '14px 16px',
                    margin: '16px 0 24px', textAlign: 'left',
                }}>
                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                        <strong>{inviteInfo?.ownerName}</strong> อยากให้คุณช่วยเป็นกำลังใจ ❤️
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                        📋 สิ่งที่ทำได้หลังยอมรับ:
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                        • ดูสถิติการเลิกบุหรี่แบบ realtime<br />
                        • ติดตามความก้าวหน้าทุกวัน
                    </div>
                </div>

                {/* Accept button */}
                <Button
                    block
                    loading={accepting}
                    onClick={handleAccept}
                    style={{
                        borderRadius: 16, height: 48,
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        border: 'none', color: 'white',
                        fontSize: 16, fontWeight: 700,
                        boxShadow: '0 8px 24px rgba(34, 197, 94, 0.3)',
                    }}
                >
                    ✅ ยอมรับคำเชิญ
                </Button>

                {/* Decline */}
                <button
                    onClick={onDone}
                    style={{
                        marginTop: 12, background: 'none', border: 'none',
                        color: '#94a3b8', fontSize: 13, cursor: 'pointer',
                    }}
                >
                    ไม่ใช่ตอนนี้
                </button>
            </div>

            {/* Branding */}
            <div style={{ marginTop: 24, color: '#94a3b8', fontSize: 11 }}>
                Porddee — ตัวช่วยเลิกสูบ 🌿
            </div>
        </div>
    );
};
